/**
 * Making a path absolute, in one place.
 *
 * Two surfaces need the same lexical resolution and must agree on it: the
 * filesystem plugin turns what a user typed in its filter bar into a path the
 * server can stat, and the chat transcript turns the relative paths an agent
 * writes into something a file preview can be pointed at. Both are "join it
 * onto a directory if it isn't already rooted, then collapse `.` and `..`",
 * and both must produce a path the server's own `normalizePath` will accept —
 * which rejects anything not rooted at `/`.
 *
 * Resolution is *lexical*: it never touches a filesystem, so it can't follow a
 * symlink or know that a segment doesn't exist. That is the right trade here —
 * the caller is resolving a name to hand to something that will do the real
 * lookup and report its own errors.
 *
 * This lives in `@reflex/shared` rather than in either consumer because the
 * alternative is two copies of "what `..` above the root means", and a
 * disagreement there resolves a path to a file nobody asked for.
 */

/**
 * The one walk over a path's segments: collapse `.`, `..`, and repeated
 * separators, and report whether a `..` had nothing left to pop.
 *
 * `..` above the root stops at the root, as POSIX does — `/..` is `/`, not an
 * error. But the clamp is worth reporting, because it is the one case where
 * resolution is not reversible: `/etc/hosts` and `/../../etc/hosts` collapse to
 * the same string, so a caller that resolves a token *to name a file* can tell
 * the difference from one that only needs a canonical string.
 */
function walkFilePath(path: string): { segments: string[]; clampedAtRoot: boolean } {
  const segments: string[] = [];
  let clampedAtRoot = false;
  for (const segment of path.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      if (segments.length === 0) clampedAtRoot = true;
      else segments.pop();
    } else segments.push(segment);
  }
  return { segments, clampedAtRoot };
}

/**
 * Collapse `.`, `..`, and repeated separators, rooted at `/`.
 *
 * `..` above the root stops at the root, as POSIX does — `/..` is `/`, not an
 * error and not an escape. A trailing separator is dropped, so a directory and
 * the same directory with a slash resolve to one string.
 */
export function normalizeFilePath(path: string): string {
  return `/${walkFilePath(path).segments.join('/')}`;
}

/**
 * The absolute, normalized form of `path`, resolved against `baseDir` when it
 * isn't already rooted. An absolute `path` ignores `baseDir` entirely, so a
 * caller can pass both without checking which it has.
 *
 * `baseDir` is assumed absolute; it is the directory a caller already knows
 * (the listing being browsed, the directory a session runs in), not user input.
 */
export function resolveFilePath(path: string, baseDir: string): string {
  return normalizeFilePath(path.startsWith('/') ? path : `${baseDir}/${path}`);
}

/**
 * Like {@link resolveFilePath}, but null when the token climbs above the root.
 *
 * For anything that turns a path *found in text* into an affordance — a chip, a
 * preview, an open event — clamping is the wrong answer. `/a/../b` is an
 * ordinary path a tool prints and resolves honestly to `/b`, so it stays. But
 * `/../b` and `/a/../../b` only reach `/b` because the walk ran out of segments
 * to pop, and pointing a reader at `/b` on that basis names a file the text
 * never did. Two tokens that mean different things would light up as the same
 * link. Declining costs a link on a token nobody meant as a path; resolving it
 * anyway opens the wrong file.
 *
 * Callers that are *navigating* rather than naming (a filter bar walking a
 * listing, where the clamp is exactly the "you're already at the top" behavior a
 * user expects) want {@link resolveFilePath} instead.
 */
export function resolveFilePathWithinRoot(path: string, baseDir = '/'): string | null {
  const { segments, clampedAtRoot } = walkFilePath(
    path.startsWith('/') ? path : `${baseDir}/${path}`,
  );
  return clampedAtRoot ? null : `/${segments.join('/')}`;
}
